import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Calculator, ArrowRightLeft } from "lucide-react";
import { MoneyInput } from "@/components/MoneyInput";
import EmployeePicker from "@/components/EmployeePicker";

const fmt = (n) => "₦" + Number(n || 0).toLocaleString("en-NG", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const inputCls = "w-full border border-ink/15 py-2.5 px-3 text-sm focus:border-klein focus:outline-none";

function Check({ checked, onChange, label, testid, disabled }) {
    return (
        <label className={`inline-flex items-center gap-2 ${disabled ? "opacity-50" : "cursor-pointer"}`}>
            <input type="checkbox" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} className="accent-klein" data-testid={testid} />
            <span>{label}</span>
        </label>
    );
}

function Tile({ label, value, sub, dark, testid }) {
    return (
        <div className={dark ? "bg-klein text-white p-4" : "bg-white p-4"} data-testid={testid}>
            <div className={`overline ${dark ? "!text-white/70" : ""}`}>{label}</div>
            <div className="mt-1 font-display font-bold text-lg tabular-nums">{value}</div>
            {sub && <div className={`text-[10px] mt-0.5 ${dark ? "text-white/60" : "text-ink/40"}`}>{sub}</div>}
        </div>
    );
}

function Result({ r, direction }) {
    const deductions = [["Pension", r.pension], ["PAYE", r.paye], ...(r.nhf ? [["NHF", r.nhf]] : []), ...(r.hmo ? [["HMO", r.hmo]] : [])];
    return (
        <div className="px-6 md:px-8 pb-6 space-y-3" data-testid="calc-result">
            {r.employee && <div className="text-xs text-ink/60" data-testid="calc-employee-label">Worked out for <strong className="text-ink">{r.employee.name}</strong>{r.employee.employee_no ? ` · ${r.employee.employee_no}` : ""} using their salary and settings.</div>}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-ink/10 border border-ink/10">
                <Tile label="Gross" value={fmt(r.gross)} sub={r.allowances ? `+ ${fmt(r.allowances)} other allowance` : ""} testid="calc-gross" />
                <Tile label="Taxable pay" value={fmt(r.taxable_total)} sub={`Pension base ${fmt(r.pensionable_base)}`} testid="calc-taxable" />
                <Tile label="Deductions" value={fmt(deductions.reduce((a, [, v]) => a + Number(v || 0), 0))} sub={deductions.map(([k, v]) => `${k} ${fmt(v)}`).join(" · ")} testid="calc-deductions" />
                <Tile label="Net pay" value={fmt(r.net)} sub={`${r.effective_tax_rate}% effective tax`} dark testid="calc-net" />
            </div>
            <div className="border border-ink/10" data-testid="calc-structure">
                <div className="px-4 py-2 bg-bone/40 overline">Company salary structure</div>
                <table className="w-full text-sm">
                    <tbody>
                        {(r.salary_breakdown || []).map((row) => (
                            <tr key={row.key || row.label} className="border-t border-ink/5">
                                <td className="px-4 py-2 text-ink">{row.label}</td>
                                <td className="px-4 py-2 text-xs text-ink/50">{row.taxable === false ? "Not taxed" : "Taxed"}{row.pensionable ? " · Pensionable" : ""}</td>
                                <td className="px-4 py-2 text-right tabular-nums">{fmt(row.amount)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <div className="text-xs text-ink/50 flex flex-wrap gap-x-4 gap-y-1">
                <span>Annual gross: <strong className="text-ink">{fmt(r.annual_gross)}</strong></span>
                <span>Annual PAYE: <strong className="text-ink">{fmt(r.annual_paye)}</strong></span>
                {r.rent_relief > 0 && <span className="text-klein" data-testid="calc-rent-relief">Rent relief: <strong>{fmt(r.rent_relief)}/yr</strong></span>}
                {direction === "net_to_gross" && <span className="text-klein">Computed gross to hit your target net.</span>}
            </div>
        </div>
    );
}

export default function TaxCalculator() {
    const [direction, setDirection] = useState("gross_to_net");
    const [employees, setEmployees] = useState([]);
    const [employeeId, setEmployeeId] = useState("");
    const [f, setF] = useState({ amount: "", allowances: "", annual_rent: "", hmo_amount: "", is_expat: false, is_non_payroll: false, nhf_enabled: false });
    const [result, setResult] = useState(null);
    const [busy, setBusy] = useState(false);
    const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
    const usingEmp = !!employeeId;
    const pickEmployee = (id) => {
        setEmployeeId(id);
        setResult(null);
        const e = employees.find((x) => x.id === id);
        if (!e) return setF((x) => ({ ...x, amount: "", allowances: "", annual_rent: "", hmo_amount: "", nhf_enabled: false, is_expat: false, is_non_payroll: false }));
        setF({
            amount: direction === "gross_to_net" ? String(e.salary || 0) : "", allowances: String(e.other_allowance || 0),
            annual_rent: String(e.annual_rent || 0), hmo_amount: String(e.hmo_amount || 0), nhf_enabled: !!e.nhf_enabled,
            is_expat: /expat/i.test(e.group || ""), is_non_payroll: /non/i.test(e.category || ""),
        });
    };

    useEffect(() => {
        api.get("/employees", { params: { status: "active", page_size: 5000 } })
            .then(({ data }) => setEmployees(Array.isArray(data) ? data : (data.rows || data.items || [])))
            .catch(() => {});
    }, []);

    const calc = async (e) => {
        e?.preventDefault?.();
        const amount = Number(f.amount) || 0;
        if (!(usingEmp && direction === "gross_to_net") && amount <= 0) return toast.error("Enter a positive amount");
        setBusy(true);
        try {
            const { data } = await api.post("/payroll/calc/tax", {
                direction, amount, employee_id: employeeId || null,
                allowances: Number(f.allowances) || 0, annual_rent: Number(f.annual_rent) || 0, hmo_amount: Number(f.hmo_amount) || 0,
                is_expat: f.is_expat, is_non_payroll: f.is_non_payroll, nhf_enabled: f.nhf_enabled,
            });
            setResult(data);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Calculation failed");
        } finally { setBusy(false); }
    };

    const amountLabel = direction === "gross_to_net" ? (usingEmp ? "Monthly gross (from profile)" : "Monthly gross (₦)") : "Target monthly net (₦)";
    return (
        <div className="mt-12 bg-white border border-ink/10" data-testid="tax-calculator">
            <div className="px-6 md:px-8 py-6 border-b border-ink/10 flex items-end justify-between flex-wrap gap-4">
                <div>
                    <div className="overline">Quick estimate</div>
                    <h2 className="mt-2 font-display font-bold text-xl text-ink flex items-center gap-2"><Calculator className="w-5 h-5 text-klein" /> Tax &amp; Net pay calculator</h2>
                    <p className="text-xs text-ink/50 mt-1">Uses your company salary structure (Settings), taxable/pensionable flags, NHF and HMO — same maths as payroll.</p>
                </div>
                <button type="button" onClick={() => { setDirection((d) => (d === "gross_to_net" ? "net_to_gross" : "gross_to_net")); setResult(null); }}
                    className="inline-flex items-center gap-2 px-4 py-2 border border-ink/15 text-xs uppercase tracking-[0.15em] hover:bg-ink/5" data-testid="calc-flip">
                    <ArrowRightLeft className="w-3.5 h-3.5" /> {direction === "gross_to_net" ? "Gross → Net" : "Net → Gross"}
                </button>
            </div>
            <form onSubmit={calc} className="px-6 md:px-8 py-6 grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
                <div className="md:col-span-4">
                    <label className="overline block mb-2">Employee (optional)</label>
                    <EmployeePicker employees={employees} value={employeeId} onChange={pickEmployee} allLabel="No employee — enter figures" className="py-2.5" testid="calc-employee" />
                </div>
                <div className="md:col-span-4">
                    <label className="overline block mb-2">{amountLabel}</label>
                    <MoneyInput value={f.amount} onChange={(e) => set("amount", e.target.value)} disabled={usingEmp && direction === "gross_to_net"} className={inputCls + " disabled:bg-ink/5"} placeholder={usingEmp && direction === "gross_to_net" ? "Uses their salary" : "e.g. 1,500,000"} data-testid="calc-amount" />
                </div>
                <div className="md:col-span-4">
                    <label className="overline block mb-2">Other allowance (₦ · not taxed)</label>
                    <MoneyInput value={f.allowances} onChange={(e) => set("allowances", e.target.value)} disabled={usingEmp} className={inputCls + " disabled:bg-ink/5"} placeholder="0" data-testid="calc-allowances" />
                </div>
                <div className="md:col-span-4">
                    <label className="overline block mb-2">Annual rent (₦) <span className="text-klein normal-case">· tax relief</span></label>
                    <MoneyInput value={f.annual_rent} onChange={(e) => set("annual_rent", e.target.value)} disabled={usingEmp} className={inputCls + " disabled:bg-ink/5"} placeholder="e.g. 1,200,000" data-testid="calc-annual-rent" />
                </div>
                <div className="md:col-span-3">
                    <label className="overline block mb-2">HMO (monthly ₦)</label>
                    <MoneyInput value={f.hmo_amount} onChange={(e) => set("hmo_amount", e.target.value)} disabled={usingEmp} className={inputCls + " disabled:bg-ink/5"} placeholder="0" data-testid="calc-hmo" />
                </div>
                <div className="md:col-span-3 flex flex-col gap-1.5 text-sm">
                    <Check checked={f.nhf_enabled} onChange={(v) => set("nhf_enabled", v)} disabled={usingEmp} label="Deduct NHF (2.5% of basic)" testid="calc-nhf" />
                    <Check checked={f.is_expat} onChange={(v) => set("is_expat", v)} disabled={usingEmp} label="Expatriate (no pension)" testid="calc-expat" />
                    <Check checked={f.is_non_payroll} onChange={(v) => set("is_non_payroll", v)} disabled={usingEmp} label="Non-payroll (no PAYE / pension)" testid="calc-nonpayroll" />
                </div>
                <div className="md:col-span-2">
                    <button type="submit" disabled={busy} className="w-full inline-flex items-center justify-center gap-2 bg-klein text-white py-3 text-sm hover:bg-klein/90 disabled:opacity-60" data-testid="calc-submit">
                        <Calculator className="w-4 h-4" /> {busy ? "Calculating…" : "Calculate"}
                    </button>
                </div>
            </form>
            {result && <Result r={result} direction={direction} />}
        </div>
    );
}
